import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { Trash2, Shield, User as UserIcon, KeyRound, Mail, Search, Loader2, Upload, FileSpreadsheet, CheckCircle, XCircle, RefreshCw, Download, Plus, Pencil, X, AlertTriangle, Building, ChevronDown, ChevronRight, FileCheck, Clock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsersManagement = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [caaList, setCaaList] = useState([]);
  const [ufficiMap, setUfficiMap] = useState({});
  const [allUffici, setAllUffici] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Import Excel Dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showImportConfirmDialog, setShowImportConfirmDialog] = useState(false);
  const [existingUsersInImport, setExistingUsersInImport] = useState([]);
  
  // Export Excel
  const [exporting, setExporting] = useState(false);
  
  // Set Password Dialog
  const [showSetPasswordDialog, setShowSetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  
  // Create/Edit User Dialog
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // null = create, object = edit
  const [userForm, setUserForm] = useState({
    full_name: '',
    email: '',
    role: 'user',
    caa_id: '',
    selected_uffici: [], // Array of {code, name, ufficio_id?}
    new_uffici: [{ code: '', name: '' }] // Array per nuovi uffici
  });
  const [savingUser, setSavingUser] = useState(false);
  
  // CAA/Uffici Management
  const [showCaaManagement, setShowCaaManagement] = useState(false);
  const [editingCaa, setEditingCaa] = useState(null);
  const [editingUfficio, setEditingUfficio] = useState(null);
  const [newCaaName, setNewCaaName] = useState('');
  const [newUfficioData, setNewUfficioData] = useState({ caa_id: '', code: '', name: '' });
  
  // Legal Acceptance Report
  const [showAcceptanceReport, setShowAcceptanceReport] = useState(false);
  const [acceptanceReport, setAcceptanceReport] = useState(null);
  const [loadingAcceptance, setLoadingAcceptance] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search users when debounced search term changes
  useEffect(() => {
    searchUsers(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const searchUsers = async (term) => {
    try {
      setSearching(true);
      const headers = { Authorization: `Bearer ${token}` };
      const params = term.trim() ? { search: term.trim() } : {};
      const usersRes = await axios.get(`${API}/users`, { headers, params });
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Errore nella ricerca');
    } finally {
      setSearching(false);
    }
  };

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, caaRes] = await Promise.all([
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/caa`, { headers })
      ]);
      setUsers(usersRes.data);
      setCaaList(caaRes.data);
      
      // Fetch uffici for each CAA
      const ufficiPromises = caaRes.data.map(caa => 
        axios.get(`${API}/caa/${caa.caa_id}/uffici`, { headers })
          .then(res => ({ caa_id: caa.caa_id, uffici: res.data }))
      );
      const ufficiResults = await Promise.all(ufficiPromises);
      const uffMap = {};
      const allUff = [];
      ufficiResults.forEach(r => { 
        uffMap[r.caa_id] = r.uffici;
        allUff.push(...r.uffici);
      });
      setUfficiMap(uffMap);
      setAllUffici(allUff);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  // Fetch acceptance report
  const fetchAcceptanceReport = async () => {
    try {
      setLoadingAcceptance(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/legal/acceptance-report`, { headers });
      setAcceptanceReport(response.data);
    } catch (error) {
      console.error('Error fetching acceptance report:', error);
      toast.error('Errore nel caricamento del report accettazioni');
    } finally {
      setLoadingAcceptance(false);
    }
  };

  // Toggle acceptance section
  const toggleAcceptanceReport = () => {
    if (!showAcceptanceReport && !acceptanceReport) {
      fetchAcceptanceReport();
    }
    setShowAcceptanceReport(!showAcceptanceReport);
  };

  // Import users from Excel
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Seleziona un file Excel (.xlsx o .xls)');
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Seleziona un file Excel (.xlsx o .xls)');
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  const importUsers = async () => {
    if (!importFile) {
      toast.error('Seleziona un file Excel');
      return;
    }

    // Prima leggiamo il file per verificare se ci sono utenti esistenti
    try {
      const data = await importFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      // Estrai le email dal file
      const emailsInFile = jsonData
        .map(row => (row.Email || row.email || '').toString().toLowerCase().trim())
        .filter(email => email);
      
      // Verifica quali email esistono già
      const existingEmails = users
        .map(u => u.email.toLowerCase())
        .filter(email => emailsInFile.includes(email));
      
      if (existingEmails.length > 0) {
        // Mostra il dialog di conferma
        setExistingUsersInImport(existingEmails);
        setShowImportConfirmDialog(true);
        return;
      }
      
      // Se non ci sono utenti esistenti, procedi direttamente
      await executeImport();
    } catch (error) {
      console.error('Error reading Excel file:', error);
      toast.error('Errore nella lettura del file Excel');
    }
  };

  const executeImport = async () => {
    setImporting(true);
    setImportResult(null);
    setShowImportConfirmDialog(false);
    
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };
      
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(`${API}/users/import`, formData, { headers });
      setImportResult(response.data);
      
      if (response.data.created > 0 || response.data.updated > 0) {
        toast.success(`Importazione completata: ${response.data.created} creati, ${response.data.updated} aggiornati`);
        fetchData();
      }
      
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} errori durante l'importazione`);
      }
    } catch (error) {
      console.error('Error importing users:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'importazione');
    } finally {
      setImporting(false);
    }
  };

  const closeImportDialog = () => {
    setShowImportDialog(false);
    setImportFile(null);
    setImportResult(null);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Export users to Excel
  const exportUsers = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}/users/export`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        // Clone response to safely read error message
        const errorText = await response.text();
        let errorMessage = 'Errore nell\'esportazione';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Response is not JSON, use default message
        }
        throw new Error(errorMessage);
      }
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'Account_e-CAA-CONFAGRICOLTURA.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Get blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Esportazione completata');
    } catch (error) {
      console.error('Error exporting users:', error);
      toast.error(error.message || 'Errore nell\'esportazione');
    } finally {
      setExporting(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/users/${userId}`, { headers });
      toast.success('Utente eliminato');
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione dell\'utente');
    }
  };

  const sendResetPasswordEmail = async (userId) => {
    if (!window.confirm('Inviare email di reset password a questo utente?')) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/users/${userId}/reset-password`, {}, { headers });
      toast.success('Email di reset password inviata');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Errore nell\'invio dell\'email');
    }
  };

  const openSetPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowSetPasswordDialog(true);
  };

  const generateRandomPassword = () => {
    // Password sicura che rispetta i criteri:
    // - Minimo 8 caratteri
    // - Almeno una maiuscola, una minuscola, un numero, un carattere speciale
    const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lowercase = 'abcdefghjkmnpqrstuvwxyz';
    const numbers = '23456789';
    const special = '!@#$%&*?';
    
    // Garantisce almeno uno di ogni tipo
    let password = '';
    password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    password += special.charAt(Math.floor(Math.random() * special.length));
    
    // Completa con caratteri casuali fino a 12 caratteri
    const allChars = uppercase + lowercase + numbers + special;
    for (let i = 0; i < 8; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    
    // Mescola la password per non avere pattern prevedibili
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setNewPassword(password);
  };

  const validatePassword = (pwd) => {
    if (!pwd || pwd.length < 8) return false;
    if (!/[A-Z]/.test(pwd)) return false;
    if (!/[a-z]/.test(pwd)) return false;
    if (!/[0-9]/.test(pwd)) return false;
    if (!/[!@#$%&*?]/.test(pwd)) return false;
    return true;
  };

  const setUserPassword = async () => {
    if (!validatePassword(newPassword)) {
      toast.error('La password deve avere almeno 8 caratteri, una maiuscola, una minuscola, un numero e un carattere speciale (!@#$%&*?)');
      return;
    }

    setSettingPassword(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/users/${selectedUser.user_id}/set-password`,
        { password: newPassword },
        { headers }
      );
      
      if (response.data.warning) {
        toast.warning(response.data.message);
      } else {
        toast.success('Password impostata ed email inviata');
      }
      
      setShowSetPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error setting password:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'impostazione della password');
    } finally {
      setSettingPassword(false);
    }
  };

  // Open dialog for creating new user
  const openCreateUserDialog = () => {
    setEditingUser(null);
    setUserForm({
      full_name: '',
      email: '',
      role: 'user',
      caa_id: '',
      selected_uffici: [],
      new_uffici: [{ code: '', name: '' }]
    });
    setShowUserDialog(true);
  };

  // Open dialog for editing existing user
  const openEditUserDialog = (user) => {
    setEditingUser(user);
    
    // Get ufficio details - now supports multiple
    const uffici = getUfficiDetails(user);
    const selectedUffici = uffici.map(u => ({
      code: u.code,
      name: u.name,
      ufficio_id: u.ufficio_id
    }));
    
    setUserForm({
      full_name: user.full_name || '',
      email: user.email || '',
      role: user.role || 'user',
      caa_id: user.caa_id || '',
      selected_uffici: selectedUffici,
      new_uffici: [{ code: '', name: '' }]
    });
    setShowUserDialog(true);
  };

  // Save user (create or update)
  const saveUser = async () => {
    // Validation
    if (!userForm.full_name.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    if (!userForm.email.trim()) {
      toast.error('L\'email è obbligatoria');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)) {
      toast.error('Email non valida');
      return;
    }

    setSavingUser(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build uffici_caa array from selected offices
      const ufficiCaaIds = userForm.selected_uffici
        .filter(u => u.ufficio_id)
        .map(u => u.ufficio_id);
      
      // Filtra i nuovi uffici che hanno sia codice che nome compilati
      const validNewUffici = userForm.new_uffici
        .filter(u => u.code.trim() && u.name.trim())
        .map(u => ({ code: u.code.trim(), name: u.name.trim() }));
      
      const payload = {
        full_name: userForm.full_name.trim(),
        email: userForm.email.trim().toLowerCase(),
        role: userForm.role,
        caa_name: userForm.caa_id ? getCaaName(userForm.caa_id) : '',
        caa_id: userForm.caa_id || null,
        // For multiple existing offices - always send array, never null
        uffici_caa: ufficiCaaIds,
        // For multiple new offices
        new_uffici: validNewUffici.length > 0 ? validNewUffici : null
      };

      if (editingUser) {
        // Update existing user
        await axios.patch(`${API}/users/${editingUser.user_id}`, payload, { headers });
        toast.success('Account aggiornato');
      } else {
        // Create new user
        await axios.post(`${API}/users`, payload, { headers });
        toast.success('Account creato. Email con credenziali inviata.');
      }

      setShowUserDialog(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingUser(false);
    }
  };

  // Get uffici for selected CAA in form
  const getFormUffici = () => {
    if (!userForm.caa_id) return [];
    return ufficiMap[userForm.caa_id] || [];
  };

  // === CAA/Uffici Management Functions ===
  
  const canDeleteCaa = (caaId) => {
    // Check if any user is assigned to this CAA
    return !users.some(u => u.caa_id === caaId);
  };
  
  const canDeleteUfficio = (ufficioId) => {
    // Check if any user is assigned to this Ufficio
    return !users.some(u => (u.uffici_caa || []).includes(ufficioId));
  };
  
  const handleDeleteCaa = async (caaId) => {
    if (!canDeleteCaa(caaId)) {
      toast.error('Impossibile eliminare: ci sono utenti assegnati a questo CAA');
      return;
    }
    
    if (!window.confirm('Sei sicuro di voler eliminare questo CAA e tutti i suoi uffici?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/caa/${caaId}`, { headers });
      toast.success('CAA eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione del CAA');
    }
  };
  
  const handleDeleteUfficio = async (caaId, ufficioId) => {
    if (!canDeleteUfficio(ufficioId)) {
      toast.error('Impossibile eliminare: ci sono utenti assegnati a questo ufficio');
      return;
    }
    
    if (!window.confirm('Sei sicuro di voler eliminare questo ufficio?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/caa/${caaId}/uffici/${ufficioId}`, { headers });
      toast.success('Ufficio eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione dell\'ufficio');
    }
  };
  
  const handleCreateCaa = async () => {
    if (!newCaaName.trim()) {
      toast.error('Inserisci il nome del CAA');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/caa`, { name: newCaaName.trim() }, { headers });
      toast.success('CAA creato');
      setNewCaaName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione del CAA');
    }
  };
  
  const handleCreateUfficio = async () => {
    if (!newUfficioData.caa_id || !newUfficioData.code.trim() || !newUfficioData.name.trim()) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/caa/${newUfficioData.caa_id}/uffici`, {
        code: newUfficioData.code.trim(),
        name: newUfficioData.name.trim()
      }, { headers });
      toast.success('Ufficio creato');
      setNewUfficioData({ caa_id: '', code: '', name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione dell\'ufficio');
    }
  };
  
  const handleUpdateCaa = async (caaId, newName) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/caa/${caaId}`, { name: newName }, { headers });
      toast.success('CAA aggiornato');
      setEditingCaa(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento del CAA');
    }
  };
  
  const handleUpdateUfficio = async (caaId, ufficioId, data) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/caa/${caaId}/uffici/${ufficioId}`, data, { headers });
      toast.success('Ufficio aggiornato');
      setEditingUfficio(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento dell\'ufficio');
    }
  };
  
  // Export CAA/Uffici to Excel
  const exportCaaUfficiToExcel = () => {
    const data = [];
    
    caaList.forEach(caa => {
      const caaUffici = allUffici.filter(u => u.caa_id === caa.caa_id);
      
      if (caaUffici.length === 0) {
        // CAA without uffici
        data.push({
          'Nome CAA': caa.name,
          'Codice Ufficio': '',
          'Nome Ufficio': '',
          'Utenti Assegnati (CAA)': users.filter(u => u.caa_id === caa.caa_id).length
        });
      } else {
        caaUffici.forEach(ufficio => {
          const usersInUfficio = users.filter(u => (u.uffici_caa || []).includes(ufficio.ufficio_id)).length;
          data.push({
            'Nome CAA': caa.name,
            'Codice Ufficio': ufficio.code || '',
            'Nome Ufficio': ufficio.name || '',
            'Utenti Assegnati (Ufficio)': usersInUfficio
          });
        });
      }
    });
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CAA_Uffici');
    XLSX.writeFile(wb, `CAA_Uffici_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('File Excel scaricato');
  };
  
  // Helper function to normalize names (Title Case)
  const normalizeName = (name) => {
    if (!name) return '';
    return name.trim().split(/\s+/).map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Import CAA/Uffici from Excel
  const handleImportCaaUffici = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);
      
      let created = 0;
      let errors = 0;
      const headers = { Authorization: `Bearer ${token}` };
      
      // Refresh CAA list first
      await fetchData();
      
      for (const row of rows) {
        const caaName = row['Nome CAA']?.toString().trim();
        const ufficioCode = row['Codice Ufficio']?.toString().trim();
        const ufficioName = row['Nome Ufficio']?.toString().trim();
        
        if (!caaName) continue;
        
        try {
          // Find CAA (case-insensitive)
          let caa = caaList.find(c => c.name.toLowerCase() === caaName.toLowerCase());
          
          if (!caa) {
            // Create CAA - backend will normalize the name
            try {
              const response = await axios.post(`${API}/caa`, { name: caaName }, { headers });
              caa = response.data; // Get the created CAA
              created++;
              // Update local list
              setCaaList(prev => [...prev, caa]);
            } catch (createErr) {
              if (createErr.response?.data?.detail?.includes('già esistente')) {
                // CAA exists, refetch to get it
                const caaResponse = await axios.get(`${API}/caa`, { headers });
                const updatedList = caaResponse.data;
                caa = updatedList.find(c => c.name.toLowerCase() === caaName.toLowerCase());
              } else {
                errors++;
                continue;
              }
            }
          }
          
          // If ufficio data provided and CAA exists, create ufficio
          if (ufficioCode && ufficioName && caa) {
            try {
              await axios.post(`${API}/caa/${caa.caa_id}/uffici`, {
                code: ufficioCode,
                name: ufficioName
              }, { headers });
              created++;
            } catch (err) {
              // Ufficio might already exist
              if (!err.response?.data?.detail?.includes('già esistente')) {
                errors++;
              }
            }
          }
        } catch (err) {
          errors++;
        }
      }
      
      toast.success(`Import completato: ${created} elementi creati${errors > 0 ? `, ${errors} errori` : ''}`);
      fetchData();
    } catch (error) {
      toast.error('Errore nella lettura del file Excel');
    }
    
    e.target.value = '';
  };
  
  // Count users for CAA/Ufficio
  const getUsersCountForCaa = (caaId) => {
    return users.filter(u => u.caa_id === caaId).length;
  };
  
  const getUsersCountForUfficio = (ufficioId) => {
    return users.filter(u => (u.uffici_caa || []).includes(ufficioId)).length;
  };

  // Users are now filtered server-side
  const filteredUsers = users;

  const getCaaName = (caaId) => {
    const caa = caaList.find(c => c.caa_id === caaId);
    return caa?.name || '-';
  };

  const getUfficiDetails = (user) => {
    // Now backend always returns unified 'uffici_caa' field
    const ufficiIds = user.uffici_caa || [];
    if (!user.caa_id || ufficiIds.length === 0) {
      return [];
    }
    const uffici = ufficiMap[user.caa_id] || [];
    return uffici.filter(u => ufficiIds.includes(u.ufficio_id));
  };

  // Component to render uffici with popover for multiple
  const UfficiCell = ({ user, field }) => {
    const uffici = getUfficiDetails(user);
    
    if (uffici.length === 0) {
      return <span className="text-slate-400">-</span>;
    }
    
    if (uffici.length === 1) {
      const value = field === 'code' ? uffici[0]?.code : uffici[0]?.name;
      return (
        <span className={field === 'code' ? 'font-mono' : ''}>
          {value || '-'}
        </span>
      );
    }
    
    // Multiple uffici - show popover
    const firstValue = field === 'code' ? uffici[0]?.code : uffici[0]?.name;
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button 
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors text-sm ${field === 'code' ? 'font-mono' : ''}`}
            data-testid={`uffici-popover-${user.user_id}-${field}`}
          >
            <span className="truncate max-w-[100px]">{firstValue}</span>
            <span className="inline-flex items-center justify-center bg-blue-600 text-white text-xs font-bold rounded-full w-5 h-5 flex-shrink-0">
              {uffici.length}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="px-3 py-2 bg-slate-100 border-b font-medium text-sm text-slate-700">
            {field === 'code' ? 'Codici Ufficio' : 'Nomi Ufficio'} ({uffici.length})
          </div>
          <div className="max-h-48 overflow-y-auto">
            {uffici.map((uff, idx) => (
              <div 
                key={uff.ufficio_id || idx} 
                className={`px-3 py-2 text-sm ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${field === 'code' ? 'font-mono' : ''}`}
              >
                {field === 'code' ? uff.code : uff.name}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3BA99C]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900" data-testid="users-management-title">
            Gestione Account
          </h1>
          <p className="text-slate-500 mt-2">Gestisci gli account utente</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={openCreateUserDialog}
            className="bg-[#3BA99C] hover:bg-[#2D8A7F] text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2"
            data-testid="create-user-btn"
          >
            <Plus className="w-5 h-5" />
            Nuovo Account
          </button>
          <button
            onClick={exportUsers}
            disabled={exporting}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2"
            data-testid="export-users-btn"
          >
            {exporting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
            Esporta Excel
          </button>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setShowImportDialog(true)}
                  className="bg-slate-600 hover:bg-slate-700 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2"
                  data-testid="import-users-btn"
                >
                  <Upload className="w-5 h-5" />
                  Importa Excel
                </button>
              </TooltipTrigger>
              <TooltipContent 
                side="bottom" 
                className="max-w-md p-3 bg-slate-900 text-white border-0 shadow-xl"
                sideOffset={8}
              >
                <div className="space-y-2">
                  <p className="font-semibold text-sm">Formato file Excel richiesto:</p>
                  <ul className="text-xs space-y-1 opacity-90">
                    <li>• <strong>Nome</strong> - Nome completo (obbligatorio)</li>
                    <li>• <strong>Email</strong> - Indirizzo email (obbligatorio)</li>
                    <li>• <strong>Ruolo</strong> - "Admin" o "Utente"</li>
                    <li>• <strong>CAA</strong> - Nome struttura CAA</li>
                    <li>• <strong>Codice Ufficio CAA</strong> - Codice ufficio</li>
                    <li>• <strong>Ufficio CAA</strong> - Nome ufficio</li>
                  </ul>
                  <div className="mt-2 pt-2 border-t border-slate-700">
                    <p className="text-xs text-amber-300 font-medium">⚠️ Per più uffici:</p>
                    <p className="text-xs opacity-90 mt-1">Separa i valori con la <strong>virgola</strong> (,)</p>
                    <p className="text-xs opacity-75 mt-1">Es: "MI001, MI002" e "Milano Centro, Milano Nord"</p>
                  </div>
                  <p className="text-xs text-emerald-300 mt-2">💡 CAA e Uffici inesistenti verranno creati automaticamente</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            {searching ? (
              <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3BA99C] animate-spin" />
            ) : (
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            )}
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca per nome, email, ruolo, CAA, codice ufficio o nome ufficio..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
              data-testid="search-users-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Utenti ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="users-table">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-slate-50">
                  <th className="text-center py-3 px-4 font-semibold text-slate-600 text-sm whitespace-nowrap">Nome</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600 text-sm whitespace-nowrap">Email</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600 text-sm whitespace-nowrap">Ruolo</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600 text-sm whitespace-nowrap">CAA</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600 text-sm whitespace-nowrap">Codice Ufficio CAA</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600 text-sm whitespace-nowrap">Ufficio CAA</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600 text-sm whitespace-nowrap">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${user.user_id}`}>
                    <td className="py-3 px-4 align-middle">
                      <div className="flex items-center gap-2">
                        {/* Pulsante modifica - nascosto se admin normale cerca di modificare super_admin */}
                        {(currentUser?.role === 'super_admin' || user.role !== 'super_admin') ? (
                          <button
                            onClick={() => openEditUserDialog(user)}
                            className="w-7 h-7 flex items-center justify-center hover:bg-amber-50 text-amber-600 rounded transition-colors flex-shrink-0"
                            title="Modifica Account"
                            data-testid={`edit-user-${user.user_id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <div className="w-7 h-7 flex-shrink-0" title="Non puoi modificare un Super Admin" />
                        )}
                        <div className="w-9 h-9 rounded-full bg-[#3BA99C] text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                          {user.full_name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-900">{user.full_name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 align-middle text-center text-slate-600">{user.email}</td>
                    <td className="py-3 px-4 align-middle text-center">
                      <span className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                        user.role === 'super_admin'
                          ? 'bg-red-100 text-red-700'
                          : user.role === 'admin'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {(user.role === 'admin' || user.role === 'super_admin') ? <Shield className="w-3.5 h-3.5" /> : <UserIcon className="w-3.5 h-3.5" />}
                        {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Utente'}
                      </span>
                    </td>
                    <td className="py-3 px-4 align-middle text-center">
                      <span className="text-sm text-slate-600">
                        {getCaaName(user.caa_id)}
                      </span>
                    </td>
                    <td className="py-3 px-4 align-middle text-center text-sm text-slate-600">
                      <UfficiCell user={user} field="code" />
                    </td>
                    <td className="py-3 px-4 align-middle text-center text-sm text-slate-600">
                      <UfficiCell user={user} field="name" />
                    </td>
                    <td className="py-3 px-4 align-middle">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openSetPasswordDialog(user)}
                          className="w-9 h-9 flex items-center justify-center hover:bg-green-50 text-green-600 rounded-lg transition-colors"
                          title="Imposta Password"
                          data-testid={`set-password-${user.user_id}`}
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => sendResetPasswordEmail(user.user_id)}
                          className="w-9 h-9 flex items-center justify-center hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                          title="Invia Email Reset Password"
                          data-testid={`reset-password-${user.user_id}`}
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        {/* Pulsante elimina - nascosto per se stessi e se admin normale cerca di eliminare super_admin */}
                        {currentUser.user_id !== user.user_id && (currentUser?.role === 'super_admin' || user.role !== 'super_admin') ? (
                          <button
                            onClick={() => deleteUser(user.user_id)}
                            className="w-9 h-9 flex items-center justify-center hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                            title="Elimina Utente"
                            data-testid={`delete-user-${user.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <div className="w-9 h-9" title={user.role === 'super_admin' && currentUser?.role !== 'super_admin' ? "Non puoi eliminare un Super Admin" : ""} /> 
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Set Password Dialog */}
      <Dialog open={showSetPasswordDialog} onOpenChange={setShowSetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Imposta Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-600">
              Imposta una nuova password per <strong>{selectedUser?.full_name}</strong>. 
              L'utente riceverà un'email con la nuova password.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nuova Password *</label>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
                placeholder="Minimo 6 caratteri"
              />
            </div>
            
            <button
              type="button"
              onClick={generateRandomPassword}
              className="text-sm text-[#3BA99C] hover:text-[#2D8A7F] font-medium"
            >
              Genera password casuale
            </button>
            
            {newPassword && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Password che verrà inviata:</p>
                <p className="font-mono text-lg text-slate-900">{newPassword}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetPasswordDialog(false)}>
              Annulla
            </Button>
            <Button onClick={setUserPassword} disabled={settingPassword || !newPassword} className="bg-[#3BA99C] hover:bg-[#2D8A7F]">
              {settingPassword ? 'Salvataggio...' : 'Imposta e Invia Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Excel Dialog */}
      <Dialog open={showImportDialog} onOpenChange={closeImportDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#3BA99C]" />
              Importa Utenti da Excel
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            {/* File format info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800 mb-2">Formato file richiesto:</p>
              <p className="text-sm text-blue-700">
                Il file Excel deve contenere le seguenti colonne:
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1 ml-4 list-disc">
                <li><strong>Nome</strong> - Nome completo dell'utente (obbligatorio)</li>
                <li><strong>Email</strong> - Indirizzo email (obbligatorio)</li>
                <li><strong>Ruolo</strong> - "Admin" o "Utente"</li>
                <li><strong>CAA</strong> - Nome della struttura CAA</li>
                <li><strong>Codice Ufficio CAA</strong> - Codice identificativo ufficio</li>
                <li><strong>Ufficio CAA</strong> - Nome dell'ufficio</li>
              </ul>
              <p className="text-xs text-blue-600 mt-3">
                Se un CAA o Ufficio non esiste, verrà creato automaticamente.
                Se un'email esiste già, i dati dell'utente verranno aggiornati.
              </p>
            </div>

            {/* File upload */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDragging 
                  ? 'border-[#3BA99C] bg-[#3BA99C]/10' 
                  : 'border-slate-300 hover:border-[#3BA99C]'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="excel-upload"
              />
              <label htmlFor="excel-upload" className="cursor-pointer">
                {importFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-[#3BA99C]" />
                    <div className="text-left">
                      <p className="font-medium text-slate-700">{importFile.name}</p>
                      <p className="text-sm text-slate-500">
                        {(importFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setImportFile(null);
                        setImportResult(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="ml-4 text-red-500 hover:text-red-700"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-600">
                      {isDragging ? 'Rilascia il file qui' : 'Trascina qui il file oppure clicca per selezionare'}
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      Formati supportati: .xlsx, .xls
                    </p>
                  </div>
                )}
              </label>
            </div>

            {/* Import results */}
            {importResult && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-3 border-b">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">Risultato Importazione</span>
                    <div className="flex gap-4 text-sm">
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        {importResult.created} creati
                      </span>
                      <span className="flex items-center gap-1 text-blue-600">
                        <RefreshCw className="w-4 h-4" />
                        {importResult.updated} aggiornati
                      </span>
                      {importResult.errors > 0 && (
                        <span className="flex items-center gap-1 text-red-600">
                          <XCircle className="w-4 h-4" />
                          {importResult.errors} errori
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-slate-600">Riga</th>
                        <th className="px-3 py-2 text-left text-slate-600">Email</th>
                        <th className="px-3 py-2 text-left text-slate-600">Stato</th>
                        <th className="px-3 py-2 text-left text-slate-600">Messaggio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.details.map((item, idx) => (
                        <tr key={idx} className={`border-t ${item.status === 'error' ? 'bg-red-50' : ''}`}>
                          <td className="px-3 py-2 text-slate-700">{item.row}</td>
                          <td className="px-3 py-2 text-slate-700">{item.email}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                              ${item.status === 'created' ? 'bg-green-100 text-green-700' : ''}
                              ${item.status === 'updated' ? 'bg-blue-100 text-blue-700' : ''}
                              ${item.status === 'error' ? 'bg-red-100 text-red-700' : ''}
                            `}>
                              {item.status === 'created' && <CheckCircle className="w-3 h-3" />}
                              {item.status === 'updated' && <RefreshCw className="w-3 h-3" />}
                              {item.status === 'error' && <XCircle className="w-3 h-3" />}
                              {item.status === 'created' ? 'Creato' : item.status === 'updated' ? 'Aggiornato' : 'Errore'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-600 text-xs">{item.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={closeImportDialog}>
              {importResult ? 'Chiudi' : 'Annulla'}
            </Button>
            {!importResult && (
              <Button 
                onClick={importUsers} 
                disabled={!importFile || importing}
                className="bg-[#3BA99C] hover:bg-[#2D8A7F]"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importazione...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importa
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {editingUser ? <Pencil className="w-5 h-5 text-amber-600" /> : <Plus className="w-5 h-5 text-[#3BA99C]" />}
              {editingUser ? 'Modifica Account' : 'Nuovo Account'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nome Completo <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={userForm.full_name}
                onChange={(e) => setUserForm(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Mario Rossi"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
                data-testid="user-form-name"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="mario.rossi@esempio.it"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
                data-testid="user-form-email"
              />
            </div>

            {/* Ruolo */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ruolo</label>
              <Select value={userForm.role} onValueChange={(value) => setUserForm(prev => ({ 
                ...prev, 
                role: value,
                // Reset CAA e uffici se si passa ad admin o super_admin
                ...((value === 'admin' || value === 'super_admin') ? { caa_id: '', selected_uffici: [], new_uffici: [{ code: '', name: '' }] } : {})
              }))}>
                <SelectTrigger className="w-full bg-white border-slate-200" data-testid="user-form-role">
                  <SelectValue placeholder="Seleziona ruolo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Utente</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  {currentUser?.role === 'super_admin' && (
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* CAA - solo per utenti non-admin */}
            {userForm.role !== 'admin' && userForm.role !== 'super_admin' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">CAA</label>
                <Select value={userForm.caa_id || "none"} onValueChange={(value) => setUserForm(prev => ({ ...prev, caa_id: value === "none" ? "" : value, selected_uffici: [], new_uffici: [{ code: '', name: '' }] }))}>
                  <SelectTrigger className="w-full bg-white border-slate-200" data-testid="user-form-caa">
                    <SelectValue placeholder="Seleziona CAA..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleziona CAA...</SelectItem>
                    {caaList.map((caa, index) => (
                      <SelectItem key={`caa-form-${index}`} value={caa.caa_id}>{caa.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Uffici CAA - selezione multipla - solo se CAA selezionato e non admin/super_admin */}
            {userForm.role !== 'admin' && userForm.role !== 'super_admin' && userForm.caa_id && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-700">Uffici CAA</label>
                
                {/* Lista uffici esistenti con checkbox */}
                {getFormUffici().length > 0 && (
                  <div className="border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto bg-slate-50">
                    <p className="text-xs text-slate-500 mb-2">Seleziona uno o più uffici:</p>
                    <div className="space-y-2">
                      {getFormUffici().map((ufficio, index) => {
                        const isSelected = userForm.selected_uffici.some(u => u.ufficio_id === ufficio.ufficio_id);
                        return (
                          <label 
                            key={`ufficio-check-${ufficio.ufficio_id}-${index}`} 
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${isSelected ? 'bg-[#3BA99C]/10 border border-[#3BA99C]' : 'bg-white border border-slate-200 hover:bg-slate-100'}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setUserForm(prev => ({
                                    ...prev,
                                    selected_uffici: [...prev.selected_uffici, {
                                      code: ufficio.code,
                                      name: ufficio.name,
                                      ufficio_id: ufficio.ufficio_id
                                    }]
                                  }));
                                } else {
                                  setUserForm(prev => ({
                                    ...prev,
                                    selected_uffici: prev.selected_uffici.filter(u => u.ufficio_id !== ufficio.ufficio_id)
                                  }));
                                }
                              }}
                              className="w-4 h-4 rounded border-slate-300 text-[#3BA99C] focus:ring-[#3BA99C]"
                            />
                            <span className="text-sm">
                              <span className="font-medium">{ufficio.code}</span>
                              <span className="text-slate-500"> - {ufficio.name}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Uffici selezionati badge */}
                {userForm.selected_uffici.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {userForm.selected_uffici.map((ufficio) => (
                      <span 
                        key={`selected-${ufficio.ufficio_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[#3BA99C]/10 text-[#3BA99C] rounded-full text-xs font-medium"
                      >
                        {ufficio.code} - {ufficio.name}
                        <button
                          type="button"
                          onClick={() => setUserForm(prev => ({
                            ...prev,
                            selected_uffici: prev.selected_uffici.filter(u => u.ufficio_id !== ufficio.ufficio_id)
                          }))}
                          className="hover:bg-[#3BA99C]/20 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Aggiunta nuovi uffici - righe dinamiche */}
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <p className="text-xs text-slate-500 mb-2">Oppure crea nuovi uffici:</p>
                  {userForm.new_uffici.map((ufficio, index) => (
                    <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2">
                      <input
                        type="text"
                        value={ufficio.code}
                        onChange={(e) => {
                          const newUffici = [...userForm.new_uffici];
                          newUffici[index].code = e.target.value;
                          setUserForm(prev => ({ ...prev, new_uffici: newUffici }));
                        }}
                        placeholder="Codice (es. MI001)"
                        className="px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C] text-sm"
                        data-testid={`user-form-new-ufficio-code-${index}`}
                      />
                      <input
                        type="text"
                        value={ufficio.name}
                        onChange={(e) => {
                          const newUffici = [...userForm.new_uffici];
                          newUffici[index].name = e.target.value;
                          // Se questa è l'ultima riga e l'utente sta scrivendo, aggiungi una nuova riga vuota
                          if (index === newUffici.length - 1 && e.target.value.trim()) {
                            newUffici.push({ code: '', name: '' });
                          }
                          setUserForm(prev => ({ ...prev, new_uffici: newUffici }));
                        }}
                        placeholder="Nome ufficio"
                        className="px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C] text-sm"
                        data-testid={`user-form-new-ufficio-name-${index}`}
                      />
                      {userForm.new_uffici.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newUffici = userForm.new_uffici.filter((_, i) => i !== index);
                            if (newUffici.length === 0) newUffici.push({ code: '', name: '' });
                            setUserForm(prev => ({ ...prev, new_uffici: newUffici }));
                          }}
                          className="px-2 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Rimuovi riga"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setUserForm(prev => ({ 
                      ...prev, 
                      new_uffici: [...prev.new_uffici, { code: '', name: '' }]
                    }))}
                    className="text-xs text-[#3BA99C] hover:text-[#2D8A7F] flex items-center gap-1 mt-1"
                  >
                    <Plus className="w-3 h-3" />
                    Aggiungi altro ufficio
                  </button>
                </div>
              </div>
            )}

            {/* Info per nuovo utente */}
            {!editingUser && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Alla creazione dell'account verrà inviata un'email all'utente con le credenziali di accesso.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => setShowUserDialog(false)} disabled={savingUser}>
              Annulla
            </Button>
            <Button 
              onClick={saveUser} 
              disabled={savingUser}
              className="bg-[#3BA99C] hover:bg-[#2D8A7F]"
            >
              {savingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                editingUser ? 'Salva Modifiche' : 'Crea Account'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CAA/Uffici Management Section */}
      <Card className="mt-6">
        <CardHeader 
          className="cursor-pointer select-none"
          onClick={() => setShowCaaManagement(!showCaaManagement)}
        >
          <CardTitle className="flex items-center justify-between text-lg">
            <div className="flex items-center gap-2">
              <Building className="w-5 h-5 text-[#5941F5]" />
              Gestione CAA e Uffici
            </div>
            {showCaaManagement ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        
        {showCaaManagement && (
          <CardContent className="space-y-6">
            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
              <p className="font-medium mb-1">ℹ️ Questa sezione è opzionale</p>
              <p className="text-blue-700">
                I CAA possono essere creati anche tramite <strong>import Excel utenti</strong>, e gli Uffici CAA 
                possono essere aggiunti durante la <strong>creazione di un account</strong> o tramite <strong>import Excel</strong>. 
                Questa sezione è stata aggiunta per permettere una gestione rapida e diretta di CAA e Uffici.
              </p>
            </div>
            
            {/* Export/Import buttons */}
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                onClick={exportCaaUfficiToExcel}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Esporta CAA/Uffici
              </Button>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleImportCaaUffici}
                        className="hidden"
                      />
                      <span className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium">
                        <Upload className="w-4 h-4" />
                        Importa CAA/Uffici
                      </span>
                    </label>
                  </TooltipTrigger>
                  <TooltipContent 
                    side="bottom" 
                    className="max-w-sm p-3 bg-slate-900 text-white border-0 shadow-xl"
                    sideOffset={8}
                  >
                    <div className="space-y-2">
                      <p className="font-semibold text-sm">Formato file Excel richiesto:</p>
                      <ul className="text-xs space-y-1 opacity-90">
                        <li>• <strong>Nome CAA</strong> - Nome della struttura CAA</li>
                        <li>• <strong>Codice Ufficio</strong> - Codice identificativo ufficio</li>
                        <li>• <strong>Nome Ufficio</strong> - Nome completo ufficio</li>
                      </ul>
                      <p className="text-xs text-emerald-300 mt-2">💡 Usa "Esporta CAA/Uffici" per ottenere un template già pronto da compilare</p>
                      <p className="text-xs text-amber-300">⚠️ CAA e Uffici già esistenti verranno ignorati</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            {/* Create new CAA */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Nuovo CAA</h4>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCaaName}
                  onChange={(e) => setNewCaaName(e.target.value)}
                  placeholder="Nome del nuovo CAA"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C] text-sm"
                />
                <Button onClick={handleCreateCaa} className="bg-[#3BA99C] hover:bg-[#2D8A7F]">
                  <Plus className="w-4 h-4 mr-1" />
                  Crea
                </Button>
              </div>
            </div>
            
            {/* Create new Ufficio */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Nuovo Ufficio CAA</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <Select 
                  value={newUfficioData.caa_id || "none"} 
                  onValueChange={(v) => setNewUfficioData(prev => ({ ...prev, caa_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Seleziona CAA" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleziona CAA</SelectItem>
                    {caaList.map(caa => (
                      <SelectItem key={caa.caa_id} value={caa.caa_id}>{caa.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input
                  type="text"
                  value={newUfficioData.code}
                  onChange={(e) => setNewUfficioData(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="Codice (es. MI001)"
                  className="px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C] text-sm"
                />
                <input
                  type="text"
                  value={newUfficioData.name}
                  onChange={(e) => setNewUfficioData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nome ufficio"
                  className="px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3BA99C] text-sm"
                />
                <Button onClick={handleCreateUfficio} className="bg-[#3BA99C] hover:bg-[#2D8A7F]">
                  <Plus className="w-4 h-4 mr-1" />
                  Crea
                </Button>
              </div>
            </div>
            
            {/* List of CAAs with their Uffici */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-slate-700">CAA e Uffici Esistenti</h4>
              <p className="text-xs text-slate-500 mb-2">
                Gli elementi con utenti assegnati non possono essere modificati o eliminati.
              </p>
              {caaList.map(caa => {
                const caaUffici = allUffici.filter(u => u.caa_id === caa.caa_id);
                const usersInCaa = getUsersCountForCaa(caa.caa_id);
                const canDeleteCaaItem = usersInCaa === 0; // Solo eliminazione bloccata se ci sono utenti
                
                return (
                  <div key={caa.caa_id} className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* CAA Header */}
                    <div className={`px-4 py-3 flex items-center justify-between ${usersInCaa > 0 ? 'bg-amber-50' : 'bg-slate-100'}`}>
                      {editingCaa === caa.caa_id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="text"
                            defaultValue={caa.name}
                            id={`edit-caa-${caa.caa_id}`}
                            className="flex-1 px-2 py-1 rounded border border-slate-300 text-sm"
                          />
                          <button
                            onClick={() => {
                              const input = document.getElementById(`edit-caa-${caa.caa_id}`);
                              handleUpdateCaa(caa.caa_id, input.value);
                            }}
                            className="text-green-600 hover:bg-green-50 p-1 rounded"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingCaa(null)}
                            className="text-slate-400 hover:bg-slate-200 p-1 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-[#5941F5]" />
                            <span className="font-medium text-slate-800">{caa.name}</span>
                            <span className="text-xs text-slate-500">({caaUffici.length} uffici)</span>
                            {usersInCaa > 0 && (
                              <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                                {usersInCaa} utenti assegnati
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Modifica sempre permessa */}
                            <button
                              onClick={() => setEditingCaa(caa.caa_id)}
                              className="text-amber-600 hover:bg-amber-50 p-1.5 rounded"
                              title="Modifica CAA"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {/* Eliminazione solo se nessun utente assegnato */}
                            {canDeleteCaaItem ? (
                              <button
                                onClick={() => handleDeleteCaa(caa.caa_id)}
                                className="text-red-600 hover:bg-red-50 p-1.5 rounded"
                                title="Elimina CAA"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            ) : (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-slate-300 p-1.5 cursor-not-allowed">
                                      <Trash2 className="w-4 h-4" />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Non eliminabile: {usersInCaa} utenti assegnati</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Uffici List */}
                    {caaUffici.length > 0 && (
                      <div className="divide-y divide-slate-100">
                        {caaUffici.map(ufficio => {
                          const usersInUfficio = getUsersCountForUfficio(ufficio.ufficio_id);
                          const canDeleteUfficioItem = usersInUfficio === 0; // Solo eliminazione bloccata
                          
                          return (
                            <div key={ufficio.ufficio_id} className={`px-4 py-2 flex items-center justify-between ${usersInUfficio > 0 ? 'bg-amber-50/50' : 'bg-white hover:bg-slate-50'}`}>
                              {editingUfficio === ufficio.ufficio_id ? (
                                <div className="flex items-center gap-2 flex-1">
                                  <input
                                    type="text"
                                    defaultValue={ufficio.code}
                                    id={`edit-uff-code-${ufficio.ufficio_id}`}
                                    className="w-24 px-2 py-1 rounded border border-slate-300 text-sm"
                                    placeholder="Codice"
                                  />
                                  <input
                                    type="text"
                                    defaultValue={ufficio.name}
                                    id={`edit-uff-name-${ufficio.ufficio_id}`}
                                    className="flex-1 px-2 py-1 rounded border border-slate-300 text-sm"
                                    placeholder="Nome"
                                  />
                                  <button
                                    onClick={() => {
                                      const code = document.getElementById(`edit-uff-code-${ufficio.ufficio_id}`).value;
                                      const name = document.getElementById(`edit-uff-name-${ufficio.ufficio_id}`).value;
                                      handleUpdateUfficio(caa.caa_id, ufficio.ufficio_id, { code, name });
                                    }}
                                    className="text-green-600 hover:bg-green-50 p-1 rounded"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setEditingUfficio(null)}
                                    className="text-slate-400 hover:bg-slate-200 p-1 rounded"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-mono bg-slate-100 px-2 py-0.5 rounded">{ufficio.code}</span>
                                    <span className="text-sm text-slate-700">{ufficio.name}</span>
                                    {usersInUfficio > 0 && (
                                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                        {usersInUfficio} utenti
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {/* Modifica sempre permessa */}
                                    <button
                                      onClick={() => setEditingUfficio(ufficio.ufficio_id)}
                                      className="text-amber-600 hover:bg-amber-50 p-1 rounded"
                                      title="Modifica Ufficio"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    {/* Eliminazione solo se nessun utente assegnato */}
                                    {canDeleteUfficioItem ? (
                                      <button
                                        onClick={() => handleDeleteUfficio(caa.caa_id, ufficio.ufficio_id)}
                                        className="text-red-600 hover:bg-red-50 p-1 rounded"
                                        title="Elimina Ufficio"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    ) : (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="text-slate-300 p-1 cursor-not-allowed">
                                              <Trash2 className="w-3.5 h-3.5" />
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Non eliminabile: {usersInUfficio} utenti assegnati</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {caaUffici.length === 0 && (
                      <div className="px-4 py-3 text-sm text-slate-400 italic">
                        Nessun ufficio per questo CAA
                      </div>
                    )}
                  </div>
                );
              })}
              
              {caaList.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  Nessun CAA presente
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Legal Acceptance Report Section */}
      <Card className="mt-6">
        <CardHeader 
          className="cursor-pointer select-none"
          onClick={toggleAcceptanceReport}
        >
          <CardTitle className="flex items-center justify-between text-lg">
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-[#3BA99C]" />
              Stato Accettazione Documenti
              {acceptanceReport && (
                <span className="text-sm font-normal text-slate-500 ml-2">
                  ({acceptanceReport.accepted_count}/{acceptanceReport.total_users} utenti)
                </span>
              )}
            </div>
            {showAcceptanceReport ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        
        {showAcceptanceReport && (
          <CardContent>
            {loadingAcceptance ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : acceptanceReport ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg">
                    <CheckCircle className="w-4 h-4" />
                    <span>{acceptanceReport.accepted_count} accettati</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg">
                    <Clock className="w-4 h-4" />
                    <span>{acceptanceReport.pending_count} in attesa</span>
                  </div>
                  <div className="text-slate-500 flex items-center gap-1">
                    <span>Versione Privacy: v{acceptanceReport.current_privacy_version}</span>
                    <span className="mx-1">|</span>
                    <span>Termini: v{acceptanceReport.current_terms_version}</span>
                  </div>
                </div>

                {/* Users Table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Utente</th>
                        <th className="text-center py-2 px-3 font-medium text-slate-600">Ruolo</th>
                        <th className="text-center py-2 px-3 font-medium text-slate-600">Stato</th>
                        <th className="text-center py-2 px-3 font-medium text-slate-600">Data Accettazione</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {acceptanceReport.users.map(u => (
                        <tr key={u.user_id} className={u.has_accepted ? '' : 'bg-amber-50/50'}>
                          <td className="py-2 px-3">
                            <div className="font-medium text-slate-800">{u.full_name}</div>
                            <div className="text-xs text-slate-500">{u.email}</div>
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              u.role === 'super_admin' ? 'bg-red-100 text-red-700' :
                              u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {u.role === 'super_admin' ? 'Super Admin' : u.role === 'admin' ? 'Admin' : 'Utente'}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            {u.has_accepted ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600">
                                <CheckCircle className="w-4 h-4" />
                                Accettato
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-amber-600">
                                <Clock className="w-4 h-4" />
                                In attesa
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center text-slate-500">
                            {u.acceptance_date ? (
                              new Date(u.acceptance_date).toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">Nessun dato disponibile</p>
            )}
          </CardContent>
        )}
      </Card>

      {/* Dialog Conferma Import con utenti esistenti */}
      <Dialog open={showImportConfirmDialog} onOpenChange={setShowImportConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Attenzione: Account già esistenti
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-slate-600">
              Il file contiene <strong>{existingUsersInImport.length}</strong> account già presenti nel sistema. 
              Proseguendo, questi account verranno <strong>aggiornati</strong> con i dati del file Excel.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 max-h-40 overflow-y-auto">
              <p className="text-xs text-amber-700 font-medium mb-2">Account che verranno modificati:</p>
              <ul className="text-sm text-amber-800 space-y-1">
                {existingUsersInImport.map((email, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    {email}
                  </li>
                ))}
              </ul>
            </div>
            
            <p className="text-sm text-slate-500">
              Sei sicuro di voler continuare con l'importazione?
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowImportConfirmDialog(false)}
            >
              Annulla
            </Button>
            <Button 
              onClick={executeImport}
              disabled={importing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importazione...
                </>
              ) : (
                'Continua Importazione'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
